import { UndefinedOr } from './utils.type';
import { INERT_MEDIA_QUERY_LIST } from './enums';

export type BrowserCallback = (...args: Array<unknown>) => unknown;
interface DeviceInfo {
    touch: boolean;
    mobile: boolean;
    reducedMotion: boolean;
}

// While TypeScript doesn't support navigation.connection interface yet, we define it here as mock.
interface NetworkInformation extends EventTarget {
    readonly type?: string;
    readonly effectiveType?: string;
    readonly downlinkMax?: number;
    readonly downlink?: number;
    readonly rtt?: number;
    readonly saveData?: boolean;
    onchange?: EventListener;
}

/**
 * @memberof SharedComponents
 * @class Browser
 * @alias browser
 */
export class Browser {
    mobileMedia!: MediaQueryList;
    tabletMedia!: MediaQueryList;
    desktopMedia!: MediaQueryList;
    hoverCapableMedia!: MediaQueryList;

    private readonly hoverCapableChangeListeners!: Array<BrowserCallback>;
    private removeHoverCapableChangeHandler!: (() => void) | null;
    private deviceInfo!: DeviceInfo;

    static readonly MediaBreakpoint: Record<string, string> = {
        Mobile: '(max-width: 767px)',
        Tablet: '(min-width: 768px) and (max-width: 1023.98px)',
        Desktop: '(min-width: 1024px)',
    };

    /**
     * Cached environment check for module initialization and constructor setup.
     * Use browser.isBrowserRuntime() for checks performed after initialization.
     */
    static readonly isBrowserEnv =
        typeof window !== 'undefined' &&
        typeof window.matchMedia === 'function' &&
        typeof window.customElements !== 'undefined' &&
        typeof document !== 'undefined' &&
        typeof document.addEventListener === 'function' &&
        typeof document.removeEventListener === 'function';

    /**
     * Checks the current browser capabilities at call time.
     * Use this for runtime operations that may run after browser APIs change.
     */
    isBrowserRuntime(): boolean {
        return (
            typeof window !== 'undefined' &&
            typeof window.matchMedia === 'function' &&
            typeof window.customElements !== 'undefined' &&
            typeof document !== 'undefined' &&
            typeof document.addEventListener === 'function' &&
            typeof document.removeEventListener === 'function'
        );
    }

    constructor() {
        this.hoverCapableChangeListeners = [];
        if (Browser.isBrowserEnv) {
            this.mobileMedia = window.matchMedia(
                Browser.MediaBreakpoint.Mobile
            );
            this.tabletMedia = window.matchMedia(
                Browser.MediaBreakpoint.Tablet
            );
            this.desktopMedia = window.matchMedia(
                Browser.MediaBreakpoint.Desktop
            );
            this.hoverCapableMedia = window.matchMedia(
                '(any-hover: hover) and (any-pointer: fine)'
            );
            this.deviceInfo = this.getDeviceInfo();
            window.addEventListener(
                'resize',
                this.handleViewportResize.bind(this)
            );
        } else {
            this.mobileMedia = INERT_MEDIA_QUERY_LIST;
            this.tabletMedia = INERT_MEDIA_QUERY_LIST;
            this.desktopMedia = INERT_MEDIA_QUERY_LIST;
            this.hoverCapableMedia = INERT_MEDIA_QUERY_LIST;
            this.deviceInfo = {
                touch: false,
                mobile: false,
                reducedMotion: false,
            };
        }
    }

    /**
     * Checks if the browser support touch
     * @returns {Record<string, boolean>}
     */
    getDeviceInfo(): DeviceInfo {
        return {
            touch: this.supportTouch(),
            mobile: this.isMobile(),
            reducedMotion: this.prefersReducedMotion(),
        };
    }

    /**
     * Checks if the browser support touch
     * @returns {boolean}
     */
    supportTouch(): boolean {
        if (!this.isBrowserRuntime()) {
            return false;
        }
        return Boolean(
            'ontouchstart' in window ||
            'onmsgesturechange' in window ||
            navigator.maxTouchPoints
        );
    }

    /**
     * Check if user has slow or cellular connection
     * Reference for spec:
     * http://wicg.github.io/netinfo/
     * @returns {boolean}
     * @deprecated
     */
    isSlowOrCellularConnection(): boolean {
        if (!this.isBrowserRuntime() || !('connection' in navigator)) {
            return false;
        }
        const connection = navigator.connection as NetworkInformation;
        if (connection) {
            if ('cellular' == connection.type) {
                return true;
            }

            // effectiveType:  'slow-2g', '2g', '3g', or '4g'.
            if (
                ['slow-2g', '2g', '3g'].includes(
                    String(connection.effectiveType)
                )
            ) {
                return false;
            }
        }
        return false;
    }

    /**
     * Checks if the browser is mobile
     * @returns {boolean}
     */
    isMobile(): boolean {
        if (!this.isBrowserRuntime()) {
            return false;
        }
        const toMatch = [
            /Android/i,
            /webOS/i,
            /iPhone/i,
            /iPad/i,
            /iPod/i,
            /BlackBerry/i,
            /Windows Phone/i,
        ];
        return toMatch.some((toMatchItem) =>
            navigator.userAgent.match(toMatchItem)
        );
    }

    /**
     * Is desktop screen width
     * @returns {boolean}
     */
    desktopMediaMatches(): boolean {
        if (!this.isBrowserRuntime()) {
            return false;
        }
        return this.desktopMedia?.matches ?? false;
    }

    /**
     * Is tablet screen width
     * @returns {boolean}
     */
    tabletMediaMatches(): boolean {
        if (!this.isBrowserRuntime()) {
            return false;
        }
        return this.tabletMedia?.matches ?? false;
    }

    /**
     * Is mobile screen width
     * @returns {boolean}
     */
    mobileMediaMatches(): boolean {
        if (!this.isBrowserRuntime()) {
            return false;
        }
        return this.mobileMedia?.matches ?? false;
    }

    /**
     * Is device hover capable
     * @returns {boolean}
     */
    hoverCapableMediaMatches(): boolean {
        if (!this.isBrowserRuntime()) {
            return false;
        }
        return this.hoverCapableMedia?.matches ?? false;
    }

    /**
     * Creates a targeted media query change handler and returns cleanup
     * function for removing the created handler.
     * @param {MediaQueryList} mediaQuery
     * @param {Array<(function(): void)>} callbacks
     * @returns {(function(): void)}
     */
    addMediaQueryChangeHandler = (
        mediaQuery: MediaQueryList,
        callbacks: Array<BrowserCallback>
    ): BrowserCallback => {
        if (!this.isBrowserRuntime()) {
            return () => {
                /* noop */
            };
        }

        const mediaQueryChangeHandler = () => {
            callbacks.forEach((callback) => {
                callback();
            });
        };

        mediaQuery.addEventListener('change', mediaQueryChangeHandler);
        return () => {
            mediaQuery.removeEventListener('change', mediaQueryChangeHandler);
        };
    };

    /**
     * Adds hover capability change callback and creates
     * the event listener if not already present.
     * @param {(function(): void)} callback
     */
    addHoverCapableChangeListener(callback: BrowserCallback) {
        if (!this.isBrowserRuntime()) {
            return;
        }

        this.hoverCapableChangeListeners.push(callback);

        this.removeHoverCapableChangeHandler ??=
            this.addMediaQueryChangeHandler(
                this.hoverCapableMedia,
                this.hoverCapableChangeListeners
            );
    }

    /**
     * Removes hover capability change callback and if no more
     * left, removes the event listener entirely.
     * @param {(function(): void)} callback
     */
    removeHoverCapableChangeListener(callback: BrowserCallback) {
        this.hoverCapableChangeListeners.splice(
            this.hoverCapableChangeListeners.indexOf(callback),
            1
        );

        if (this.hoverCapableChangeListeners.length === 0) {
            this.removeHoverCapableChangeHandler?.();
            this.removeHoverCapableChangeHandler = null;
        }
    }

    /**
     * Checks if the user prefers reduced motion,
     * could be used to simplify transitions and animations
     * @returns {boolean}
     */
    prefersReducedMotion() {
        if (!this.isBrowserRuntime()) {
            return false;
        }
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    /**
     * Fires callback when window resized
     * @private
     * @fires event:browser:update
     */
    handleViewportResize() {
        if (!this.isBrowserRuntime()) {
            return;
        }

        const currentData = this.getDeviceInfo();

        const isChanged =
            this.deviceInfo.touch !== currentData.touch ||
            this.deviceInfo.mobile !== currentData.mobile ||
            this.deviceInfo.reducedMotion !== currentData.reducedMotion;

        if (!isChanged) {
            return;
        }

        this.deviceInfo = this.getDeviceInfo();

        const event = new CustomEvent('browser:update', {
            bubbles: true,
            detail: currentData,
        });
        document.body.dispatchEvent(event);
    }

    /**
     * Resolves first level domain from full domain name. Returns an empty string
     * if run not in browser.
     * @returns {string}
     */
    resolveMainDomain(): string {
        if (!this.isBrowserRuntime()) {
            return '';
        }
        const hostname = window.location.hostname;
        // check for IP address
        // ^ Start of the line
        // (?:[0-9]{1,3}\.){3}[0-9]{1,3} Matches the 4 groups of
        // [0-9]{1, 3} digits from 1 to 3
        // \. separated with dot
        // $ End of the line
        // Examples: 1.1.1.1, 22.22.22.22, 1.22.333.444, etc.
        if (/^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/.exec(hostname)) {
            return hostname;
        }

        const match = /[^.]*\.[^.]{2,3}(?:\.[^.])?$/.exec(hostname);
        return String(match ?? hostname);
    }

    /**
     * Gets the media query list for mobile, tablet and desktop breakpoints.
     * @returns {Record<string, string>}
     */
    getMediaQueryMap(): Record<string, string> {
        return Browser.MediaBreakpoint;
    }

    /**
     * Run in SSR friendly environment.
     * @param {unknown} fn Callback to execute.
     * @param {unknown} [fallback] Value to return outside a browser environment.
     * @returns {unknown} Callback result or the fallback value, or undefined when no fallback is supplied.
     */
    run<T>(fn: () => T): UndefinedOr<T>;
    run<T>(fn: () => T, fallback: T): T;
    run<T>(fn: () => T, fallback?: T): UndefinedOr<T> {
        return this.isBrowserRuntime() ? fn() : fallback;
    }
}

export const browser = new Browser();

/**
 * @memberof Browser
 * @event browser:update
 * @alias event:browser:update
 * @augments {CustomEvent}
 */
